/**
 * Traded volume of outcome markets (#874). Pure: no React, no I/O. Ported to Android (:hl Volume.kt) and iOS
 * (HLCore Volume.swift); shared/fixtures/volume.json pins all three to this code.
 *
 * Sources (documented /info endpoints only; findings in docs/research/HL_UI_ENDPOINTS.md, #874):
 *   - 24 h: spotMetaAndAssetCtxs[1], the ctx of the side-0 coin: dayNtlVlm = USDC (matches Hyperliquid's own UI),
 *     dayBaseVlm = contracts. A question's 24 h volume = the sum over its named outcomes (questionDayVolume).
 *   - last hour: candleSnapshot "1h" of the side-0 coin from the start of the previous hour: v = contracts, n = trades.
 *   - lifetime: candleSnapshot "1d" of the side-0 coin from startTime 0 (one request; 5,000 daily candles = 13 years).
 *
 * Every outcome trade is reported on BOTH side coins (#E0 and #E1 carry the same contracts and trade counts, and their
 * notionals add up to contracts x 1 USDC). So an outcome's volume is read from side 0 only, never summed over sides:
 * contracts are exact, and USDC is the Yes-side (side-0) notional, i.e. contracts x the Yes price they traded at.
 * Coins come from hl/outcomes.ts.
 */
import { coin, fromCoin } from '../hl/outcomes';
import type { Candle, SpotAssetCtx } from '../hl/types';

/** Hyperliquid keeps the 5,000 most recent candles per coin and interval. */
export const CANDLE_CAP = 5000;
export const HOUR_MS = 3_600_000;
export const HOUR_INTERVAL = '1h';
export const DAY_INTERVAL = '1d';
/** Lifetime candles are requested from the epoch: every daily candle since listing. */
export const LIFETIME_START_MS = 0;

export interface VolumeAmount {
  /** Contracts (shares) traded. */
  contracts: number;
  /** USDC traded on the Yes side (side 0). Exact for 24 h; for candles an estimate (see lifetimeVolume). */
  usd: number;
}

/** 24 h volume from the asset ctxs. */
export type DayVolume = VolumeAmount;

/** Previous complete hour + the current partial hour (from 1h candles). */
export interface HourVolume extends VolumeAmount {
  trades: number;
  /** Start of the previous complete hour (ms). */
  fromMs: number;
  /** `now` (ms): the window ends with the current, partial hour. */
  toMs: number;
}

/** Sum of every daily candle since listing (or since the oldest candle Hyperliquid still keeps). */
export interface LifetimeVolume extends VolumeAmount {
  trades: number;
  /** Candles summed. */
  candles: number;
  /** Open time of the oldest candle (ms); absent without candles. */
  firstMs?: number;
  /**
   * True when older candles may be missing (Hyperliquid's 5,000-candle cap): show the amounts as "at least".
   * Set when the answer holds `cap` candles or reaches back to the start of the requested window (never for the
   * startTime-0 lifetime request in practice, but kept so the cap is always honoured).
   */
  capped: boolean;
}

/**
 * Hook for a future "trending" flag (#874; not shown yet): the last-hour rate against the 24 h average rate, both in
 * contracts per hour. ratio > 1 = trading faster than over the last day. Null ratio when the 24 h volume is 0.
 */
export interface VolumeTrend {
  hourlyRate: number;
  dayHourlyAvg: number;
  ratio: number | null;
}

export interface MarketVolume {
  day?: DayVolume;
  lastHour?: HourVolume;
  lifetime?: LifetimeVolume;
  trend?: VolumeTrend;
}

function num(v: unknown): number | undefined {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : undefined;
}

/**
 * 24 h volume per outcome id from spotMetaAndAssetCtxs[1]. Reads only side-0 outcome coins (see the module comment);
 * non-outcome ctxs, side-1 ctxs and ctxs with unparsable volume are skipped.
 */
export function dayVolumes(ctxs: unknown): Map<number, DayVolume> {
  const out = new Map<number, DayVolume>();
  if (!Array.isArray(ctxs)) return out;
  for (const c of ctxs as Partial<SpotAssetCtx>[]) {
    if (!c || typeof c.coin !== 'string') continue;
    const ref = fromCoin(c.coin);
    if (!ref || ref.side !== 0 || !c.coin.startsWith('#')) continue;
    const contracts = num(c.dayBaseVlm);
    const usd = num(c.dayNtlVlm);
    if (contracts === undefined || usd === undefined) continue;
    out.set(ref.outcome, { contracts, usd });
  }
  return out;
}

/** The candle coin of an outcome: its side-0 coin (both sides carry the same trades). */
export function volumeCoin(outcome: number): string {
  return coin(outcome, 0);
}

export function floorHour(ms: number): number {
  return Math.floor(ms / HOUR_MS) * HOUR_MS;
}

/** Last-hour candleSnapshot ("1h") window: from the start of the previous hour to now (at most 2 candles). */
export function hourWindow(nowMs: number): { startTime: number; endTime: number } {
  return { startTime: floorHour(nowMs) - HOUR_MS, endTime: nowMs };
}

/** Sum of the side-0 24 h volume over a question's named outcomes (absent ones count 0); undefined if none known. */
export function questionDayVolume(volumes: ReadonlyMap<number, DayVolume>, outcomes: readonly number[]): DayVolume | undefined {
  let contracts = 0;
  let usd = 0;
  let any = false;
  for (const o of outcomes) {
    const v = volumes.get(o);
    if (!v) continue;
    any = true;
    contracts += v.contracts;
    usd += v.usd;
  }
  return any ? { contracts, usd } : undefined;
}

/**
 * Valid candles sorted by open time, one per open time (the last one wins). `null` / non-array answers (Hyperliquid
 * answers HTTP 500 `null` for an unknown coin) give []. A candle without a finite `t`, `v` or `c` is dropped.
 */
export function parseCandles(raw: unknown): Candle[] {
  if (!Array.isArray(raw)) return [];
  const byT = new Map<number, Candle>();
  for (const c of raw as Partial<Candle>[]) {
    if (!c || typeof c !== 'object') continue;
    const t = num(c.t);
    const v = num(c.v);
    const close = num(c.c);
    if (t === undefined || v === undefined || close === undefined || v < 0) continue;
    byT.set(t, { ...(c as Candle), t, n: num(c.n) ?? 0 });
  }
  return [...byT.values()].sort((a, b) => a.t - b.t);
}

/**
 * Last-hour volume from 1h candles: the previous complete hour plus the current, partial hour (so the figure covers
 * 60-120 minutes; the UI says so). Contracts and trades are exact; USDC is an estimate (v x close per hour).
 * `candles` must come from parseCandles.
 */
export function lastHourVolume(candles: readonly Candle[], nowMs: number): HourVolume {
  const fromMs = floorHour(nowMs) - HOUR_MS;
  let contracts = 0;
  let usd = 0;
  let trades = 0;
  for (const c of candles) {
    if (c.t < fromMs || c.t > nowMs) continue;
    const v = Number(c.v);
    contracts += v;
    usd += v * Number(c.c);
    trades += c.n;
  }
  return { contracts, usd, trades, fromMs, toMs: nowMs };
}

/**
 * Lifetime volume: the sum over every daily candle. Contracts and trades are exact; USDC is an estimate (each day's
 * contracts x that day's closing Yes price). `windowStartMs` is the startTime that was requested
 * (LIFETIME_START_MS); `cap` is Hyperliquid's candle limit (a parameter so tests can exercise the flag).
 */
export function lifetimeVolume(candles: readonly Candle[], windowStartMs: number, cap: number = CANDLE_CAP): LifetimeVolume {
  let contracts = 0;
  let usd = 0;
  let trades = 0;
  for (const c of candles) {
    const v = Number(c.v);
    contracts += v;
    usd += v * Number(c.c);
    trades += c.n;
  }
  const first = candles[0];
  const capped = candles.length >= cap || (first !== undefined && first.t <= windowStartMs);
  return { contracts, usd, trades, candles: candles.length, ...(first ? { firstMs: first.t } : {}), capped };
}

/** Trending hook (not displayed yet): last-hour rate vs the 24 h average, in contracts per hour. */
export function volumeTrend(lastHour: HourVolume, day: DayVolume): VolumeTrend {
  const hours = Math.max(1, (lastHour.toMs - lastHour.fromMs) / HOUR_MS);
  const hourlyRate = lastHour.contracts / hours;
  const dayHourlyAvg = day.contracts / 24;
  return { hourlyRate, dayHourlyAvg, ratio: dayHourlyAvg > 0 ? hourlyRate / dayHourlyAvg : null };
}

/**
 * Everything the market page shows: the ctx volume, the raw 1h answer (last hour) and the raw 1d answer (lifetime,
 * requested from `lifetimeStartMs`). `undefined` = not loaded yet (that figure is left out).
 */
export function marketVolume(
  day: DayVolume | undefined,
  rawHourCandles: unknown,
  rawDayCandles: unknown,
  nowMs: number,
  lifetimeStartMs: number = LIFETIME_START_MS,
  cap: number = CANDLE_CAP,
): MarketVolume {
  const out: MarketVolume = {};
  if (day) out.day = day;
  if (rawHourCandles !== undefined) {
    out.lastHour = lastHourVolume(parseCandles(rawHourCandles), nowMs);
    if (day) out.trend = volumeTrend(out.lastHour, day);
  }
  if (rawDayCandles !== undefined) out.lifetime = lifetimeVolume(parseCandles(rawDayCandles), lifetimeStartMs, cap);
  return out;
}

// ---- compact numbers -------------------------------------------------------------------------------------------

export type CompactUnit = '' | 'k' | 'm' | 'b' | 't';

/** A compact amount: `mantissa` is an English-formatted number ('.' decimal, no grouping); `unit` scales it. */
export interface CompactParts {
  mantissa: string;
  unit: CompactUnit;
}

const UNITS: readonly CompactUnit[] = ['', 'k', 'm', 'b', 't'];
const SCALES = [1, 1e3, 1e6, 1e9, 1e12];

function stripZeros(s: string): string {
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

/** < 1: 2 decimals; < 100: 1 decimal; else whole. JavaScript toFixed rounding (exact value, ties up). */
function mantissa(x: number): string {
  return stripZeros(x.toFixed(x < 1 ? 2 : x < 100 ? 1 : 0));
}

/**
 * 1234567 -> { mantissa: "1.2", unit: "m" }; 999.96 -> { "1", "k" }; 0 -> { "0", "" }; 0.384 -> { "0.38", "" }.
 * Null for a negative or non-finite value. Locale formatting (separators, $ placement, unit letters) is the
 * platform's job (volume/format.ts on the web).
 */
export function compactParts(n: number): CompactParts | null {
  if (!Number.isFinite(n) || n < 0) return null;
  if (n === 0) return { mantissa: '0', unit: '' };
  let i = 0;
  while (i < UNITS.length - 1 && n >= SCALES[i + 1]!) i++;
  let m = mantissa(n / SCALES[i]!);
  if (Number(m) >= 1000 && i < UNITS.length - 1) {
    i++;
    m = mantissa(n / SCALES[i]!);
  }
  return { mantissa: m, unit: UNITS[i]! };
}
